import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {Loader} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {ResourceMap} from '../../../store/appTypes';
import {ViewingNowCalendar} from '../../../store/panels/calendars/calendarsPanelState';

/*
 * Props.
 */

interface ViewingNowCalendarsListItemProps {
  calendarVersions: ResourceMap<CalendarVersionModel>;
  onAddCalendarClick?: HandlerOf<number>;
  viewingNowCalendar: ViewingNowCalendar;
  onRemoveViewingNowCalendar: HandlerOf<ViewingNowCalendar>;
  nameOverride?: string;
}

/*
 * Style.
 */

const StyledAddCalendarsIconButton = styled(IconButton)`
  display: none;
  padding: 0px;
  &:hover {
    background: none;
  }
`;

const StyledRowDiv = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 32px 1fr auto;
  height: 28px;
  border-radius: 4px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};

    ${StyledAddCalendarsIconButton} {
      display: block;
    }
  }
`;

const StyledLeftIconDiv = styled.div`
  display: grid;
  justify-content: center;
`;

interface NameStyleProps {
  $color: string;
}
const StyledNameDiv = styled.div<NameStyleProps>`
  color: ${(p) => p.$color};

  ${ellipsis()};
`;

const StyledButtonSectionDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 6px;
  justify-content: center;
  padding-left: 8px;
  padding-right: 8px;
`;

interface CircleStyleProps {
  $color: PaletteColorsEnum;
}
const StyledCircleDiv = styled.div<CircleStyleProps>`
  width: 12px;
  height: 12px;
  border-radius: 6px;
  background-color: ${(p) => p.theme.palette[p.$color].shade40};
`;

const StyledDashedCircleDiv = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 6px;
  border: 1.5px dashed ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const ViewingNowCalendarsListItem: FC<ViewingNowCalendarsListItemProps> = (props) => {
  const theme = useUserTheme();
  const {calendarVersions, onAddCalendarClick, onRemoveViewingNowCalendar, viewingNowCalendar} = props;

  const calendarVersion = viewingNowCalendar.calendarVersionId
    ? calendarVersions[viewingNowCalendar.calendarVersionId]
    : undefined;

  const hasFailed = viewingNowCalendar.status === AsyncStatusesEnum.FAILED;

  /*
   * Render.
   */

  return (
    <StyledRowDiv>
      <StyledLeftIconDiv>{renderLeftIcon(viewingNowCalendar)}</StyledLeftIconDiv>
      <TooltipCoordinator
        render={() =>
          hasFailed ? (
            <Trans id="viewingnow-calendar-not-found">Calendar not found</Trans>
          ) : (
            props.nameOverride || viewingNowCalendar.address
          )
        }
      >
        <StyledNameDiv $color={hasFailed ? theme.greys.shade70 : theme.greys.shade80}>
          <TooltipOverflowCondition overrideCondition={hasFailed}>
            {props.nameOverride || viewingNowCalendar.address}
          </TooltipOverflowCondition>
        </StyledNameDiv>
      </TooltipCoordinator>

      <StyledButtonSectionDiv>
        {maybeRenderAddCalendarButton(calendarVersion, onAddCalendarClick)}
        <TooltipCoordinator render={() => <Trans id="viewingnow-calendar-remove">Remove</Trans>}>
          <ChromelessButton onClick={() => onRemoveViewingNowCalendar(viewingNowCalendar)}>
            <Icon name="clearSmall" color={theme.greys.shade70} hoverColor={theme.greys.shade80} />
          </ChromelessButton>
        </TooltipCoordinator>
      </StyledButtonSectionDiv>
    </StyledRowDiv>
  );
};

function renderLeftIcon(viewingNowCalendar: ViewingNowCalendar) {
  switch (viewingNowCalendar.status) {
    case AsyncStatusesEnum.LOADED:
      return <StyledCircleDiv $color={viewingNowCalendar.color} />;
    case AsyncStatusesEnum.LOADING:
      return <Loader size={VisualSizesEnum.SMALL} />;
    default:
      return <StyledDashedCircleDiv />;
  }
}

function maybeRenderAddCalendarButton(
  calendarVersion: CalendarVersionModel | undefined,
  onAddCalendarClick?: HandlerOf<number>,
) {
  if (!calendarVersion || !onAddCalendarClick) {
    return null;
  }

  /*
   * Event handlers.
   */

  const onAddClick = () => {
    if (!calendarVersion.isHidden) {
      return;
    }

    onAddCalendarClick(calendarVersion.id);
  };

  /*
   * Render.
   */

  const label = calendarVersion.isHidden ? (
    <Trans id="viewingnow-calendar-add">Add to Calendars</Trans>
  ) : (
    <Trans id="viewingnow-calendar-added">Already added to Calendars</Trans>
  );

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledAddCalendarsIconButton
          iconName="addSmall"
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
          onClick={onAddClick}
          label={label}
        />
      )}
    </UserThemeConsumer>
  );
}
